import { FC, useRef, useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react-lite';
import { ContentTab } from '../../types.ts/general';
import PullQuotes from './PullQuotes';
import MediaView from './MediaView';
import BlogView from './BlogView';
import EmailView from './EmailView';
import SocialClipsView from './SocialClipsView';
import TalkingPointStudio from './TalkingPointStudio';
import TranscriptEditorTab from './TranscriptEditorTab';
import { useSearchParams } from 'react-router-dom';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import { useStore } from '../../stores-v2/StoreContext';

const ContentView: FC<{}> = observer((props) => {
  const [activeTab, setActiveTab] = useState<ContentTab>(ContentTab.video_clip);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { userIdentityStore, transcriptionStore } = useStore();
  const flags = useFlagsCombination(userIdentityStore.currentIdentity?.role as any);
  
  // Check if the transcript editor is enabled via LaunchDarkly flag and compatible with current transcript
  const isTranscriptEditorEnabled = flags.enableTranscriptEditor && transcriptionStore.isTranscriptEditorCompatible;
  
  // Check for tab parameter in URL
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    // Skip Transcript Editor tab if disabled via flag or incompatible transcript
    if (tabParam && tabParam === ContentTab.transcript_editor && !isTranscriptEditorEnabled) {
      return;
    }
    if (tabParam && Object.values(ContentTab).includes(tabParam as ContentTab)) {
      setActiveTab(tabParam as ContentTab);
    }
  }, [searchParams, isTranscriptEditorEnabled]);
  
  // Update URL when tab changes
  const handleTabChange = (tab: ContentTab) => {
    // Prevent switching to Transcript Editor if it's disabled or incompatible transcript
    if (tab === ContentTab.transcript_editor && !isTranscriptEditorEnabled) {
      return;
    }
    
    wrapperRef.current?.scrollIntoView({ behavior: 'smooth' });
    setActiveTab(tab);
    
    // Preserve existing parameters while updating the tab
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams);
  };

  return (
    <Main>
      <Tabs>
        {Object.entries(ContentTab).map(([k, v]) => {
          // Skip the Transcript Editor tab if the flag is disabled
          if (v === ContentTab.transcript_editor && !isTranscriptEditorEnabled) {
            return null;
          }
          return (
            <TabItem
              key={k}
              isSelected={activeTab === v}
              onClick={() => handleTabChange(v)}
            >
              {v}
            </TabItem>
          );
        })}
        {/* {(activeTab === ContentTab.blog || activeTab === ContentTab.email) && (
          <div className="share">
            <ContentShare />
          </div>
        )} */}
      </Tabs>

      {(() => {
        switch (activeTab) {
          case ContentTab.video_clip:
            return (
              <ContentWrapper ref={wrapperRef}>
                <SocialClipsView />
              </ContentWrapper>
            );
          case ContentTab.blog:
            return (
              <ContentWrapper ref={wrapperRef}>
                <BlogView key="blog" type="blog" />
              </ContentWrapper>
            );
          case ContentTab.social_posts:
            return (
              <ContentWrapper ref={wrapperRef}>
                <BlogView key="socialPost" type="socialPost" />
              </ContentWrapper>
            );
          case ContentTab.talking_point_studio:
            return (
              <ContentWrapper ref={wrapperRef}>
                <TalkingPointStudio />
              </ContentWrapper>
            );
          case ContentTab.media:
            return (
              <ContentWrapper ref={wrapperRef}>
                <MediaView />
              </ContentWrapper>
            );
          case ContentTab.pull_quotes:
            return (
              <ContentWrapper ref={wrapperRef}>
                <PullQuotes />
              </ContentWrapper>
            );
          case ContentTab.email:
            return (
              <ContentWrapper ref={wrapperRef}>
                <EmailView />
              </ContentWrapper>
            );
          case ContentTab.transcript_editor:
            if (isTranscriptEditorEnabled) {
              return (
                  <TranscriptEditorTab />
              );
            }
            // If flag is disabled or transcript is incompatible but somehow we ended up with this tab active,
            // redirect to default tab
            setTimeout(() => handleTabChange(ContentTab.video_clip), 0);
            return null;
          // case ContentTab.external:
          //   return (
          //     <ContentWrapper ref={wrapperRef}>
          //       <ExternalContent />
          //     </ContentWrapper>
          //   );
          default:
            return <ContentWrapper ref={wrapperRef}></ContentWrapper>;
        }
      })()}
    </Main>
  );
});

export default ContentView;

const Main = styled.div`
  margin-top: 10px;
  position: relative;
`;
const Tabs = styled.nav`
  margin-top: 20px;
  display: flex;
  justify-content: flex-start;
  gap: 30px;
  align-items: center;
  .copy,
  .share {
    margin-left: auto;
    display: flex;
    gap: 4px;
    align-items: center;
    color: #f2d093;
  }
  margin-bottom: 15px;
  border-bottom: 1px solid #484848;

  @media only screen and (max-width: 750px) {
    width: 100%;
    overflow-x: scroll;
  }
`;
const TabItem = styled.menu<{ isSelected?: boolean }>`
  display: flex;
  padding: 0;
  cursor: pointer;
  color: #f3e9d7;
  &:hover,
  &:active {
    color: #f2d093;
    border-bottom: 1px solid #f2d093;
  }

  ${(props) =>
    props.isSelected &&
    css`
      color: #f2d093;
      border-bottom: 1px solid #f2d093;
    `}
`;

const ContentWrapper = styled.div`
  min-height: 50vh;
  margin-bottom: 30px;
`;
